"use client"
import EditorWrapper from "@/components/Editor";
import {Button} from "@nextui-org/react";
import { FaRotate } from "react-icons/fa6";
import React, {FC, useState} from "react";
import {editor} from "monaco-editor";
import {CodeFormatter, HandleJSONChanges, HandleXMLChanges, HandleYAMLChanges} from "@/lib/editor";
import {ConversionDataTypes} from "@/state/features/types";
import {useAppDispatch, useAppSelector} from "@/state/hooks";
import {convert} from "@/state/features/dataConverter";
import {copyToClipboard, saveText} from "@/lib/utils";
import {Check, Copy} from "lucide-react";
import { useSplitPosition } from "@/components/ResizableSplit/useSplitPosition";
import SplitHandle from "@/components/ResizableSplit/SplitHandle";
import EditorToolbar from "@/components/Editor/EditorToolbar";
import {trackToolAction} from "@/lib/analytics";

interface ValidatorProps{
    inputLanguage: ConversionDataTypes;
    outputLanguage: ConversionDataTypes;
    startingValue: string;
}

const Converter: FC<ValidatorProps> = (props: ValidatorProps) => {
    const {inputLanguage, outputLanguage, startingValue} = props;
    const [showValidations, setShowValidations] = useState<boolean>(false)
    const [code, setCode] = useState<string>(startingValue);
    const [validationErrors, setValidationErrors] = useState<Array<editor.IMarker>>([]);
    const [copied, setCopied] = useState<boolean>(false);

    const [monacoEditor, setMonacoEditor] = useState<null | typeof editor>(null);
    const [editorInstance, setEditorInstance] = useState<null | editor.IStandaloneCodeEditor>(null);
    const outputText = useAppSelector((state) => {return state.conversions.convertedText})
    const { containerRef, percent, isDesktop, isDragging, startDrag, handleKeyDown: handleSplitKeyDown, resetToDefault } =
        useSplitPosition({ defaultPercent: 42, minPercent: 25, maxPercent: 60, storageKey: "split-converter" });

    const dispatch = useAppDispatch();

    const onConvert = () => {
        if(!!editorInstance && !!monacoEditor) {
            // first we'll validate if there are no known errors
            if(inputLanguage === "json") {
                HandleJSONChanges(code, monacoEditor, editorInstance)
            }
            else if(inputLanguage === "xml") {
                HandleXMLChanges(code, monacoEditor, editorInstance)
            }
            else if(inputLanguage === "yaml"){
                HandleYAMLChanges(code, monacoEditor, editorInstance)
            }
            const errors = monacoEditor.getModelMarkers({owner: inputLanguage});
            // if there are no know errors we'll try to convert
            if(errors.length == 0){
                dispatch(convert({originalText: code, originalTextType: inputLanguage, convertedTextType: outputLanguage}));
                CodeFormatter(code, inputLanguage)
            }
            else{
                // set validation panel since there are validation errors
                setValidationErrors(errors);
                setShowValidations(true);

            }

        }

    }

    function handleEditorChange(value: string | undefined) {
        trackToolAction("converter", "edit", `${inputLanguage}-to-${outputLanguage}`);
        if(!!value){
            setCode(value)
            setShowValidations(false);
        }
    }

    const handleValidationClick = (lineNumber: number, column: number) => {
        if(editorInstance) {
            editorInstance.setPosition({lineNumber, column});
            editorInstance.revealLineInCenter(lineNumber);
            editorInstance.focus();
        }
    }

    const handleCopy = async () => {
        if (code) {
            trackToolAction("converter", "copy", `${inputLanguage}-to-${outputLanguage}`);
            await copyToClipboard(outputText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const showPanel = showValidations && validationErrors.length > 0;

    return (
        <div className="w-full flex flex-col">
        <div className="w-full flex justify-end px-4 md:px-0">
            <EditorToolbar />
        </div>
        <div ref={containerRef} className="w-full mt-1 flex flex-col md:flex-row gap-4 md:gap-0 px-4 md:px-0 items-stretch">
            <div className="w-full min-w-0" style={isDesktop ? { width: `${percent}%` } : undefined}>
                <EditorWrapper value={code}
                               language={inputLanguage}
                               setEditor={setEditorInstance}
                               setMonacoEditor={setMonacoEditor}
                               changeHandler={handleEditorChange}
                               showValidationPanel={showValidations}
                               isValid={validationErrors.length === 0}
                />
            </div>

            <SplitHandle
                onMouseDown={startDrag}
                onTouchStart={startDrag}
                onKeyDown={handleSplitKeyDown}
                onDoubleClick={resetToDefault}
                ariaLabel="Resize input and output editors"
                percent={percent}
                isDragging={isDragging}
            />

            <div className="w-full md:flex-1 min-w-0 flex flex-col md:flex-row gap-4 items-stretch">
                {showPanel && (
                    <div className="w-full md:w-64 md:shrink-0 flex flex-col h-full border-1 dark:border-gray-700">
                        <p className="text-center py-5 border-b-1 dark:border-gray-700 dark:text-gray-100">Validation Errors</p>
                        {validationErrors.map((error) => {
                            return (
                                <div
                                    key={`${error.startColumn}${error.endColumn} - ${error.startLineNumber}${error.endLineNumber}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleValidationClick(error.startLineNumber, error.startColumn)
                                    }}
                                    className="w-full group flex items-center border-b-1 dark:border-gray-700 hover:cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"
                                         strokeWidth={1.5} stroke="currentColor"
                                         className="size-8 text-gray-500 dark:text-gray-400 group-hover:text-blue-900 dark:group-hover:text-blue-400 shrink-0">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/>
                                    </svg>
                                    <p className="text-red-500 py-5 text-sm">{error.message}</p>
                                </div>
                            )
                        })}
                    </div>
                )}

                <div className="w-full md:w-40 md:shrink-0 flex flex-row md:flex-col justify-center items-center gap-3">
                    <Button className="w-full" color="primary" radius="sm" variant="bordered" startContent={<FaRotate/>}
                            onClick={() => {
                                trackToolAction("converter", "convert", `${inputLanguage}-to-${outputLanguage}`);
                                onConvert()
                            }}
                    > Convert</Button>
                    <Button
                        className="w-full"
                        radius="sm"
                        variant="bordered"
                        color="primary"
                        onClick={handleCopy}
                        startContent={copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                    >
                        {copied ? "Copied!" : `Copy ${outputLanguage}`}
                    </Button>

                    <Button className="w-full" radius="sm" variant="bordered" color="primary" onClick={() => {
                        if(code){
                            trackToolAction("converter", "download", `${inputLanguage}-to-${outputLanguage}`);
                            saveText(outputText, outputLanguage);
                        }
                    }}>Download
                    </Button>
                </div>

                <div className="w-full md:flex-1 min-w-0 flex">
                    <EditorWrapper value={outputText}
                                   readOnly={true}
                                   language={outputLanguage}
                                   placeholder={`Click Convert to see the ${outputLanguage.toUpperCase()} output here`}
                    />
                </div>
            </div>
        </div>
        </div>
    )
}

export default Converter;
